// /apps/patient/src/pages/billing/InvoiceDetail.tsx

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import { PageHeader } from '../../components/layout/PageHeader';
import { Invoice, Payment } from '@prism-ehr/shared';
import { formatDate } from '@prism-ehr/shared';
import { cptLinesHaveMinutes, cptUnits, formatCptMinutes } from '@prism-ehr/shared/utils/cptCodes';
import {
  Download, Printer, CreditCard, ArrowLeft,
  CheckCircle, Clock, AlertCircle, FileText,
  Calendar, User, Building, Receipt, ExternalLink,
  Check, Stethoscope, ClipboardList
} from 'lucide-react';

const POS_MAP: Record<string, string> = { office: '11', telehealth: '02', home: '12', facility: '21' };

export function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(false);

  useEffect(() => {
    if (id) {
      loadInvoice();
      loadPayments();
    }
  }, [id]);

  const loadInvoice = async () => {
    try {
      const data = await patientApiService.getInvoiceDetail(id!);
      setInvoice(data);
    } catch (error) {
      console.error('Error loading invoice:', error);
      alert('Failed to load invoice');
      navigate('/billing');
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      setLoadingPayments(true);
      const response = await patientApiService.getMyPayments({ invoice_id: id });
      setPayments(response.items || []);
    } catch (error) {
      console.error('Error loading payments:', error);
      // Don't fail the page if payments can't load
    } finally {
      setLoadingPayments(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async () => {
    window.print();
  };

  const handlePayNow = () => {
    navigate(`/billing/pay/${id}`);
  };

  const getStatusIcon = () => {
    switch (invoice?.status) {
      case 'paid':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'overdue':
        return <AlertCircle className="h-6 w-6 text-red-500" />;
      case 'pending_insurance':
        return <Clock className="h-6 w-6 text-blue-500" />;
      case 'cancelled':
      case 'refunded':
        return <AlertCircle className="h-6 w-6 text-gray-400" />;
      case 'created':
        return <FileText className="h-6 w-6 text-gray-400" />;
      default:
        return <Clock className="h-6 w-6 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'overdue':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending_insurance':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
      case 'refunded':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'created':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const getPaymentStatusColor = (status: Payment['status']) => {
    switch (status) {
      case 'succeeded':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Invoice not found</h3>
          <button
            onClick={() => navigate('/billing')}
            className="mt-4 text-primary hover:underline"
          >
            Back to invoices
          </button>
        </div>
      </div>
    );
  }

  const hasInsurance = !!invoice?.insurance_claim_id;
  const hasBillingCodes = invoice?.line_items?.some(
    (item: any) => item.metadata?.cpt_codes?.length > 0 || item.metadata?.icd10_codes?.length > 0
  );
  const showMin = cptLinesHaveMinutes((invoice?.line_items || []).flatMap((item: any) => item.metadata?.cpt_codes || []));

  // Collect unique ICD-10 diagnoses across all line items
  const allDiagnoses: Array<{ code: string; description: string; is_primary: boolean }> = [];
  if (hasBillingCodes) {
    const seen = new Set<string>();
    invoice.line_items.forEach((item: any) => {
      (item.metadata?.icd10_codes || []).forEach((dx: any) => {
        if (!seen.has(dx.code)) {
          seen.add(dx.code);
          allDiagnoses.push({ code: dx.code, description: dx.description || '', is_primary: !!dx.is_primary });
        } else if (dx.is_primary) {
          const existing = allDiagnoses.find(d => d.code === dx.code);
          if (existing) existing.is_primary = true;
        }
      });
    });
    allDiagnoses.sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
  }

  // Extract visit notes data for superbill
  const visitNotes = (invoice as any).visit_notes || {};
  const visitNoteEntries = Object.entries(visitNotes) as Array<[string, any]>;
  const firstNote = visitNoteEntries.length > 0 ? visitNoteEntries[0][1] : null;
  const provider = firstNote?.provider;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          title="Invoice Details"
          showBack={true}
          actions={
            <div className="flex gap-2 print:hidden">
              <button
                onClick={handlePrint}
                className="hidden sm:inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </button>
              <button
                onClick={handleDownload}
                className="hidden sm:inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
              {invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.status !== 'pending_insurance' && (
                <button
                  onClick={handlePayNow}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay Now
                </button>
              )}
            </div>
          }
        />

        {/* Invoice Container */}
        <div className="mt-6 bg-white shadow rounded-lg p-6 sm:p-8 print:shadow-none print:border-0">
          {/* Status Badge */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {getStatusIcon()}
              <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(invoice.status)}`}>
                {invoice.status === 'pending_insurance' 
                  ? 'Pending Insurance' 
                  : invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
              </span>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Invoice Number</p>
              <p className="font-bold text-lg">#{invoice.invoice_number}</p>
            </div>
          </div>

          {/* Invoice Header */}
          <div className="border-b pb-6 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-2">From</h3>
                <div className="space-y-1">
                  <p className="font-medium flex items-center gap-2">
                    <Building className="h-4 w-4 text-gray-400" />
                    {invoice.organization?.name}
                  </p>
                  {invoice?.encounter?.provider && (
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      {invoice.encounter.provider.first_name} {invoice.encounter.provider.last_name}
                    </p>
                  )}
                </div>
              </div>

              <div className="sm:text-right">
                <h3 className="text-sm font-medium text-gray-600 mb-2">Date</h3>
                <div className="space-y-1">
                  <p className="text-sm flex items-center gap-2 sm:justify-end">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">Invoice Date:</span> {formatDate(invoice.invoice_date, 'short')}
                  </p>
                  {invoice.due_date && (
                    <p className="text-sm flex items-center gap-2 sm:justify-end">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">Due Date:</span> {formatDate(invoice.due_date, 'short')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Service Details */}
          {invoice.service_date && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Service provided on <span className="font-medium">{formatDate(invoice.service_date, 'long')}</span>
              </p>
            </div>
          )}

          {/* Insurance Information Section - Only shows if insurance_claim_id exists */}
          {hasInsurance && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-blue-600" />
                Insurance Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Claim Number</p>
                  <p className="font-medium">#{invoice.insurance_claim_id.slice(-8).toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-gray-600">Status</p>
                  <p className="font-medium">
                    {invoice.status === 'pending_insurance' ? 'Processing with Insurance' : 
                     invoice.status === 'paid' ? 'Insurance Processed' : 'Pending'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Total Service Charges</p>
                  <p className="font-medium">{formatCurrency(invoice.total_amount)}</p>
                </div>
                <div>
                  <p className="text-gray-600">
                    {invoice.status === 'pending_insurance' ? 'Estimated Insurance Coverage' : 'Insurance Paid'}
                  </p>
                  <p className="font-medium">{formatCurrency((invoice.insurance_portion || 0) + (invoice.secondary_insurance_portion || 0))}</p>
                </div>
                {invoice.status === 'pending_insurance' && (
                  <div className="col-span-1 sm:col-span-2">
                    <p className="text-xs text-blue-700 italic">
                      Note: Your actual responsibility will be determined after insurance processing.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Line Items / Superbill */}
          {hasBillingCodes ? (
            <>
              {/* Provider & Patient Info (Superbill Header) */}
              {provider && (
                <div className="mb-6 border rounded-lg p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Provider / Organization */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Rendering Provider</h4>
                      <div className="space-y-1 text-sm">
                        <p className="font-medium text-gray-900">
                          {provider.first_name} {provider.last_name}
                          {provider.credentials && <span className="text-gray-600">, {provider.credentials}</span>}
                        </p>
                        {provider.designation && (
                          <p className="text-gray-600">{provider.designation}</p>
                        )}
                        {provider.npi && (
                          <p className="text-gray-600">NPI: <span className="font-mono">{provider.npi}</span></p>
                        )}
                      </div>
                      {invoice.organization && (
                        <div className="mt-3 space-y-1 text-sm">
                          <p className="font-medium text-gray-900">{invoice.organization.name}</p>
                          {(invoice.organization as any).address && (
                            <p className="text-gray-600">{(invoice.organization as any).address}</p>
                          )}
                          {(invoice.organization as any).phone && (
                            <p className="text-gray-600">{(invoice.organization as any).phone}</p>
                          )}
                          {(invoice.organization as any).tax_id && (
                            <p className="text-gray-600">Tax ID: <span className="font-mono">{(invoice.organization as any).tax_id}</span></p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Patient */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Patient</h4>
                      <div className="space-y-1 text-sm">
                        {(invoice as any).patient && (
                          <>
                            <p className="font-medium text-gray-900">
                              {(invoice as any).patient.first_name} {(invoice as any).patient.last_name}
                            </p>
                            {(invoice as any).patient.date_of_birth && (
                              <p className="text-gray-600">DOB: {formatDate((invoice as any).patient.date_of_birth, 'short')}</p>
                            )}
                            {(invoice as any).patient.address && (
                              <p className="text-gray-600">{(invoice as any).patient.address}</p>
                            )}
                            {(invoice as any).patient.phone && (
                              <p className="text-gray-600">{(invoice as any).patient.phone}</p>
                            )}
                          </>
                        )}
                        {hasInsurance && (invoice as any).insurance_info && (
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Insurance</p>
                            <p className="text-gray-600">{(invoice as any).insurance_info.payer_name}</p>
                            {(invoice as any).insurance_info.member_id && (
                              <p className="text-gray-600">Member ID: {(invoice as any).insurance_info.member_id}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ICD-10 Diagnosis Table */}
              {allDiagnoses.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-gray-500" />
                    Diagnoses
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-gray-200 bg-gray-50">
                        <tr>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Code</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Description</th>
                          <th className="text-center py-2 px-3 font-medium text-gray-700">Primary</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {allDiagnoses.map((dx, idx) => (
                          <tr key={idx}>
                            <td className="py-2 px-3 font-mono text-gray-900">{dx.code}</td>
                            <td className="py-2 px-3 text-gray-700">{dx.description}</td>
                            <td className="py-2 px-3 text-center">
                              {dx.is_primary && <Check className="h-4 w-4 text-green-600 mx-auto" />}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* CPT Services Table */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-gray-500" />
                  Services
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-gray-200 bg-gray-50">
                      <tr>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Date</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-700">POS</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">CPT</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Description</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Modifiers</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-700">Units</th>
                        {showMin && <th className="text-center py-2 px-3 font-medium text-gray-700">Min</th>}
                        <th className="text-right py-2 px-3 font-medium text-gray-700">Charge</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {invoice.line_items.map((item: any, itemIdx: number) => {
                        const cptCodes = item.metadata?.cpt_codes || [];
                        const encounterId = item.metadata?.encounter_id;
                        const noteForItem = encounterId ? visitNotes[encounterId] : null;
                        const visitType = (noteForItem as any)?.visit_type || '';
                        const posCode = POS_MAP[visitType] || '11';
                        const serviceDate = (noteForItem as any)?.date_of_service || invoice.service_date || invoice.invoice_date;

                        if (cptCodes.length === 0) {
                          // Line item without CPT codes - show as single row
                          return (
                            <tr key={itemIdx}>
                              <td className="py-2 px-3 text-gray-700">{formatDate(serviceDate, 'short')}</td>
                              <td className="py-2 px-3 text-center text-gray-700">{posCode}</td>
                              <td className="py-2 px-3 font-mono text-gray-500">-</td>
                              <td className="py-2 px-3 text-gray-900">{item.description}</td>
                              <td className="py-2 px-3 text-gray-500">-</td>
                              <td className="py-2 px-3 text-center text-gray-700">{item.quantity}</td>
                              {showMin && <td className="py-2 px-3" />}
                              <td className="py-2 px-3 text-right font-medium text-gray-900">{formatCurrency(item.amount)}</td>
                            </tr>
                          );
                        }

                        return cptCodes.map((cpt: any, cptIdx: number) => (
                          <tr key={`${itemIdx}-${cptIdx}`}>
                            <td className="py-2 px-3 text-gray-700">{formatDate(serviceDate, 'short')}</td>
                            <td className="py-2 px-3 text-center text-gray-700">{posCode}</td>
                            <td className="py-2 px-3 font-mono font-medium text-gray-900">{cpt.code}</td>
                            <td className="py-2 px-3 text-gray-700">{cpt.description}</td>
                            <td className="py-2 px-3 font-mono text-gray-600">
                              {cpt.modifiers && cpt.modifiers.length > 0 ? cpt.modifiers.join(', ') : '-'}
                            </td>
                            <td className="py-2 px-3 text-center text-gray-700">{cptUnits(cpt)}</td>
                            {showMin && <td className="py-2 px-3 text-center text-gray-700">{formatCptMinutes(cpt)}</td>}
                            <td className="py-2 px-3 text-right font-medium text-gray-900">
                              {formatCurrency(cpt.charge_amount || (cptIdx === 0 ? item.amount : 0))}
                            </td>
                          </tr>
                        ));
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            /* Simple line items table (no billing codes) */
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Services</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-gray-200">
                    <tr>
                      <th className="text-left py-3 text-sm font-medium text-gray-900">Description</th>
                      <th className="text-center py-3 text-sm font-medium text-gray-900">Qty</th>
                      <th className="text-right py-3 text-sm font-medium text-gray-900">Rate</th>
                      <th className="text-right py-3 text-sm font-medium text-gray-900">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invoice.line_items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="py-4">
                          <p className="text-sm font-medium text-gray-900">{item.description}</p>
                          {item.service_code && (
                            <p className="text-xs text-gray-500">Code: {item.service_code}</p>
                          )}
                        </td>
                        <td className="py-4 text-center text-sm text-gray-900">{item.quantity}</td>
                        <td className="py-4 text-right text-sm text-gray-900">{formatCurrency(item.unit_price)}</td>
                        <td className="py-4 text-right text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Totals - Conditional rendering based on insurance */}
          <div className="border-t pt-6">
            <div className="flex justify-end">
              <div className="w-full sm:w-80 space-y-3">
                {!hasInsurance ? (
                  // Cash patient totals
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
                    </div>

                    {invoice.tax_amount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tax</span>
                        <span className="font-medium">{formatCurrency(invoice.tax_amount)}</span>
                      </div>
                    )}

                    {invoice.discount_amount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Discount</span>
                        <span className="font-medium text-green-600">-{formatCurrency(invoice.discount_amount)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-base font-medium pt-3 border-t">
                      <span>Total</span>
                      <span className="text-lg">{formatCurrency(invoice.total_amount)}</span>
                    </div>

                    {invoice.amount_paid > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Amount Paid</span>
                        <span className="font-medium text-green-600">-{formatCurrency(invoice.amount_paid)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-base font-bold pt-3 border-t">
                      <span>Balance Due</span>
                      <span className="text-xl text-primary">{formatCurrency(invoice.balance_due)}</span>
                    </div>
                  </>
                ) : (
                  // Insurance patient totals
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Service Charges</span>
                      <span className="font-medium">{formatCurrency(invoice.total_amount)}</span>
                    </div>

                    {invoice.insurance_portion > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {invoice.status === 'pending_insurance' ? 'Estimated Insurance Coverage' : 'Insurance Paid'}
                        </span>
                        <span className="font-medium text-blue-600">-{formatCurrency(invoice.insurance_portion)}</span>
                      </div>
                    )}
                    {(invoice.secondary_insurance_portion || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Secondary Insurance Paid</span>
                        <span className="font-medium text-blue-600">-{formatCurrency(invoice.secondary_insurance_portion)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-base font-medium pt-3 border-t">
                      <span>Your Responsibility</span>
                      <span className="text-lg">{formatCurrency(invoice.patient_responsibility || invoice.balance_due)}</span>
                    </div>

                    {invoice.amount_paid > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Amount Paid</span>
                        <span className="font-medium text-green-600">-{formatCurrency(invoice.amount_paid)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-base font-bold pt-3 border-t">
                      <span>Balance Due</span>
                      <span className="text-xl text-primary">{formatCurrency(invoice.balance_due)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Clinical Documentation (Superbill) */}
          {hasBillingCodes && visitNoteEntries.length > 0 && (
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-gray-500" />
                Clinical Documentation
              </h3>
              <div className="space-y-6">
                {visitNoteEntries.map(([encounterId, note]: [string, any]) => (
                  <div key={encounterId} className="border rounded-lg p-4">
                    {/* Note Header */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="text-sm font-medium text-gray-900">
                        {formatDate(note.date_of_service, 'long')}
                      </span>
                      {note.provider && (
                        <span className="text-sm text-gray-600">
                          &mdash; {note.provider.first_name} {note.provider.last_name}
                          {note.provider.credentials && `, ${note.provider.credentials}`}
                        </span>
                      )}
                      {note.visit_type && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                          {note.visit_type.replace(/_/g, ' ')}
                        </span>
                      )}
                      {note.signed && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                          <Check className="h-3 w-3" />
                          Signed
                        </span>
                      )}
                    </div>

                    {/* Note Content */}
                    {note.sections_data?.sections ? (
                      <div className="space-y-4">
                        {(note.sections_data.sections as any[]).map((section: any, sIdx: number) => {
                          const fields = section.fields?.filter((f: any) => f.value != null && f.value !== '' && f.value !== false);
                          if (!fields?.length) return null;
                          return (
                            <div key={sIdx}>
                              {section.sectionName && (
                                <h5 className="text-sm font-semibold text-gray-800 mb-1">{section.sectionName}</h5>
                              )}
                              <div className="space-y-1">
                                {fields.map((field: any, fIdx: number) => {
                                  let val = field.value;
                                  if (typeof val === 'object' && val !== null && !Array.isArray(val) && 'content' in val) {
                                    val = val.content;
                                    if (val == null || val === '') return null;
                                  }
                                  let display: React.ReactNode;
                                  if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object') {
                                    const cols = Object.keys(val[0]);
                                    display = (
                                      <table className="w-full text-sm border-collapse mt-1">
                                        <thead>
                                          <tr>{cols.map(c => <th key={c} className="text-left p-1 border-b text-gray-600 text-xs font-medium">{c}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                          {val.map((row: any, ri: number) => (
                                            <tr key={ri} className="border-b border-gray-100">
                                              {cols.map(c => <td key={c} className="p-1 text-gray-700">{row[c] != null && typeof row[c] === 'object' ? JSON.stringify(row[c]) : String(row[c] ?? '—')}</td>)}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    );
                                  } else if (Array.isArray(val)) {
                                    display = <span className="text-gray-700">{val.map(v => String(v)).join(', ')}</span>;
                                  } else if (typeof val === 'boolean') {
                                    display = <span className="text-gray-700">{val ? 'Yes' : 'No'}</span>;
                                  } else if (typeof val === 'object' && val !== null) {
                                    display = <span className="text-gray-700">{Object.entries(val).filter(([,v]) => v != null).map(([k,v]) => `${k}: ${v}`).join(', ')}</span>;
                                  } else {
                                    display = <span className="text-gray-700">{String(val)}</span>;
                                  }
                                  return (
                                    <div key={fIdx} className="ml-2 text-sm text-gray-700">
                                      {field.label && <span className="font-medium">{field.label}: </span>}
                                      {display}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {['subjective', 'objective', 'assessment', 'plan'].map(key => {
                          let content = note.clinical_note?.[key];
                          if (!content) return null;
                          if (typeof content === 'object' && content?.content) {
                            content = content.content;
                          }
                          if (!content) return null;
                          const label = key.charAt(0).toUpperCase() + key.slice(1);
                          return (
                            <div key={key}>
                              <h5 className="text-sm font-semibold text-gray-800 mb-1">{label}</h5>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{content}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Addendums */}
                    {note.addendums && note.addendums.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Addendums</h5>
                        <div className="space-y-2">
                          {(note.addendums as any[]).map((addendum: any, aIdx: number) => (
                            <div key={aIdx} className="bg-gray-50 rounded p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs text-gray-500">{formatDate(addendum.created_at, 'long')}</span>
                                {addendum.author && (
                                  <span className="text-xs text-gray-500">&mdash; {addendum.author}</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{addendum.content}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Signature Attestation */}
                    {note.signed && note.signature_attestation && (
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500 italic">{note.signature_attestation}</p>
                        {note.signed_at && (
                          <p className="text-xs text-gray-400 mt-1">Signed: {formatDate(note.signed_at, 'long')}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment History */}
          {payments.length > 0 && (
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Payment History</h3>
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div key={payment.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Receipt className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium">
                            {formatCurrency(payment.amount)}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPaymentStatusColor(payment.status)}`}>
                            {payment.status}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 space-y-1">
                          <p>{formatDate(payment.processed_at || payment.created_at, 'long')}</p>
                          {payment.card_last_four && (
                            <p>
                              {payment.card_brand} •••• {payment.card_last_four}
                            </p>
                          )}
                        </div>
                      </div>
                      {payment.receipt_url && payment.status === 'succeeded' && (
                        <a 
                          href={payment.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary-dark flex items-center gap-1 text-sm"
                        >
                          Receipt
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                    {payment.refunded_amount > 0 && (
                      <div className="mt-2 text-xs text-red-600">
                        Refunded: {formatCurrency(payment.refunded_amount)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {invoice.notes && (
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Notes</h3>
              <p className="text-sm text-gray-600">{invoice.notes}</p>
            </div>
          )}

          {/* Payment Terms */}
          <div className="mt-8 pt-6 border-t">
            <p className="text-xs text-gray-500">
              Payment terms: {invoice.payment_terms.replace('_', ' ')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}